import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { DEFAULT_TRANSFER_FEE_USD } from '../utils/bankService';

const ConfigContext = createContext({});

// Branding is cached per Firebase project so the app paints with the real theme/name immediately
// instead of flashing the defaults while appConfig loads. `uma.branding.last` feeds public/index.html.
const BRANDING_CACHE_KEY = `uma.branding.${db.app?.options?.projectId || 'default'}`;
const BRANDING_CACHE_LAST_KEY = 'uma.branding.last';

const readCachedBranding = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(BRANDING_CACHE_KEY) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedBranding = (branding) => {
  try {
    const raw = JSON.stringify(branding);
    localStorage.setItem(BRANDING_CACHE_KEY, raw);
    localStorage.setItem(BRANDING_CACHE_LAST_KEY, raw);
  } catch {
    // Storage unavailable (private mode / quota) — branding still loads from Firestore
  }
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export const ConfigProvider = ({ children }) => {
  const [hasCachedBranding] = useState(() => readCachedBranding() !== null);
  const [config, setConfig] = useState(() => ({
    apiKeys: [],
    branding: readCachedBranding() || {
      companyName: 'UCENA Management System',
      logo: '/assets/logo.png',
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      favicon: '/assets/favicon.ico',
      tagline: 'Streamline Your Business Operations'
    },
    finance: {
      creditCardProcessingPercent: 3.1,
      checkProcessingPercent: 1.5,
      transferFeeUsd: DEFAULT_TRANSFER_FEE_USD
    },
    legal: [],
    countries: [],
    industries: [],
    loading: true
  }));
  const [loading, setLoading] = useState(true);

  // Default configuration data for initial setup
  const defaultConfig = {
    apiKeys: [
      { id: 'stripe', name: 'Stripe API Key', value: '', environment: 'Test', status: 'Inactive', description: 'Payment processing' },
      { id: 'sendgrid', name: 'SendGrid API Key', value: '', environment: 'Production', status: 'Inactive', description: 'Email service' },
      { id: 'aws', name: 'AWS S3 Access Key', value: '', environment: 'Production', status: 'Inactive', description: 'File storage' }
    ],
    branding: {
      companyName: 'UCENA Management System',
      logo: '/assets/logo.png',
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      favicon: '/assets/favicon.ico',
      tagline: 'Streamline Your Business Operations'
    },
    finance: {
      creditCardProcessingPercent: 3.1,
      checkProcessingPercent: 1.5,
      transferFeeUsd: DEFAULT_TRANSFER_FEE_USD
    },
    legal: [
      { 
        id: 'terms', 
        type: 'Terms of Service', 
        content: 'These terms govern the use of our service and establish the legal framework for user interactions with our platform.', 
        lastUpdated: new Date().toISOString() 
      },
      { 
        id: 'privacy', 
        type: 'Privacy Policy', 
        content: 'We respect your privacy and are committed to protecting your personal information in accordance with applicable laws.', 
        lastUpdated: new Date().toISOString() 
      },
      { 
        id: 'gdpr', 
        type: 'GDPR Compliance', 
        content: 'In accordance with GDPR regulations, we ensure proper handling of personal data for EU residents.', 
        lastUpdated: new Date().toISOString() 
      }
    ],
    countries: [
      { id: 'us', name: 'United States', code: 'US', currency: 'USD', timezone: 'EST', status: 'Active' },
      { id: 'ca', name: 'Canada', code: 'CA', currency: 'CAD', timezone: 'EST', status: 'Active' },
      { id: 'gb', name: 'United Kingdom', code: 'GB', currency: 'GBP', timezone: 'GMT', status: 'Active' },
      { id: 'de', name: 'Germany', code: 'DE', currency: 'EUR', timezone: 'CET', status: 'Active' },
      { id: 'fr', name: 'France', code: 'FR', currency: 'EUR', timezone: 'CET', status: 'Active' },
      { id: 'es', name: 'Spain', code: 'ES', currency: 'EUR', timezone: 'CET', status: 'Active' },
      { id: 'jp', name: 'Japan', code: 'JP', currency: 'JPY', timezone: 'JST', status: 'Active' },
      { id: 'au', name: 'Australia', code: 'AU', currency: 'AUD', timezone: 'AEST', status: 'Active' },
      { id: 'it', name: 'Italy', code: 'IT', currency: 'EUR', timezone: 'CET', status: 'Active' },
      { id: 'nl', name: 'Netherlands', code: 'NL', currency: 'EUR', timezone: 'CET', status: 'Active' }
    ],
    industries: [
      { id: 'tech', name: 'Technology', description: 'Software, Hardware, IT Services', status: 'Active' },
      { id: 'healthcare', name: 'Healthcare', description: 'Medical, Pharmaceutical, Biotechnology', status: 'Active' },
      { id: 'finance', name: 'Finance', description: 'Banking, Insurance, Investment', status: 'Active' },
      { id: 'manufacturing', name: 'Manufacturing', description: 'Production, Assembly, Distribution', status: 'Active' },
      { id: 'consulting', name: 'Consulting', description: 'Business Advisory, Strategic Planning', status: 'Active' },
      { id: 'trading', name: 'Trading', description: 'Import/Export, Commodity Trading', status: 'Active' },
      { id: 'energy', name: 'Energy', description: 'Oil, Gas, Renewable Energy', status: 'Active' },
      { id: 'mining', name: 'Mining', description: 'Resource Extraction, Minerals', status: 'Active' },
      { id: 'education', name: 'Education', description: 'Schools, Universities, Training', status: 'Active' },
      { id: 'government', name: 'Government', description: 'Public Sector, Agencies', status: 'Active' },
      { id: 'automotive', name: 'Automotive', description: 'Vehicle Manufacturing, Parts', status: 'Active' }
    ],
    // Centralized editable copy for PDFs
    pdfTexts: {
      proposals: {
        intro: 'At UCENA, we don’t just offer services—we become partners in bringing your vision to life. With a sharp focus on product innovation, marketing strategy, and creative execution, we work alongside you to craft solutions that don’t just meet expectations but redefine them.',
        summaryTemplate: 'UCENA LLC proposes a streamlined digital solution to {clientName} to establish a professional online presence and marketing strategy.\n\nWhat\'s Included:\n\n• One-Page Website to showcase your business\n• Domain purchase, full setup, and one year of hosting\n• Integration with Instagram, Facebook, Yelp, Google My Business, and Apple Maps\n• Setup of Google Search Console and Google Analytics for performance tracking\n\nTo help manage operations smoothly, we’ll also:\n\n• Assist in choosing the right CRM for bookings and set it up with your business details\n• Provide marketing guidance to define your brand and launch effective campaigns\n• Manage ad spend as scoped in this proposal',
        additionalTemplate: 'Non-UCENA Expenditures (Client Responsibility)\n\nTo maintain and operate the digital ecosystem, the following costs are expected:\n• Advertising budget: $100–$300 per month\n• Domain purchase: Approximately $48 per year\n• Google Workspace: Around $8 per month\n• CRM & booking system costs: Varies based on the selected provider\n\nAdditional Notes:\n• UCENA will assist in selecting and configuring a CRM, but the ongoing costs will be the client’s responsibility.\n• Any advertising budget beyond the scoped period will be covered by the client.\n• This proposal sets up the digital foundation, ensuring a seamless transition into growth-focused marketing efforts.',
        discountNote: 'A special discount has been applied to this price for relationship building purposes, these do not reflect in any way the market prices or UCENA costs. We hope to review the relationship at the end of the 3rd month.',
        headings: {
          summary: 'SUMMARY',
          additional: 'KEY OPPORTUNITIES',
          description: 'DESCRIPTION',
          offer: 'Offer',
          validTo: 'Valid to',
          feesFirstMonth: 'First Month',
          feesNextMonths: "Other Months"
        }
      },
      contracts: {
        documentTitle: 'Services Agreement',
        betweenHeading: 'Between:',
        andHeading: 'And:',
        ucenaBlock: [
          'UCENA LLC',
          '30 North Gould Street',
          'Sheridan, WY 82801',
          'Email: legal@ucena.com',
          '("UCENA")'
        ],
        sections: {
          servicesAndScope: 'UCENA LLC shall provide strategic advice, coordination, and oversight for marketing services including brand storytelling, digital advertising, social media management and content production. Actual execution will be performed by independent subcontractors coordinated by UCENA. Detailed scope, specific services, and fees are outlined separately in the "Project Scope Document".',
          feesAndPaymentsIntro: 'No work begins until the first payment is received. Subsequent monthly invoices will be as follows:',
          clientObligations: 'Client shall provide timely approvals, feedback, and assets. UCENA may pause Services and continue billing if the Client causes delays.',
          expensesThirdParty: 'Client is solely responsible for third-party fees and accounts (Google, Facebook, etc.).',
          cancellation: 'Client may cancel at any time by emailing legal@ucena.com and must pay the current month plus one additional month.',
          intellectualProperty: 'Upon full payment, UCENA transfers all intellectual property rights in deliverables to Client, retaining no rights thereafter.',
          noDataCustody: 'UCENA does not store or manage Client data; all work is executed on Client-owned platforms.',
          nonSolicitation: 'Client agrees not to solicit or hire UCENA subcontractors for six months after termination.',
          independentContractors: 'UCENA engages subcontractors to perform actual work and is not liable for their actions. If Client objects to any subcontractor, they must promptly email legal@ucena.com, and UCENA will make reasonable efforts to reassign.',
          forceMajeure: 'Neither party is liable for delays or failures beyond reasonable control (natural disasters, wars, pandemics, etc.).',
          liabilityLimitation: 'UCENA’s total liability under this Agreement is limited to 20% of total fees paid and excludes indirect or consequential damages.',
          assignment: 'Client may not assign this Agreement without UCENA’s prior written consent.',
          disputeResolution: 'Disputes shall be resolved via binding arbitration by AAA rules in Los Angeles County, California. Prevailing party recovers attorney fees and costs.',
          notices: 'Official communications must be sent to legal@ucena.com. Day-to-day interactions may occur through any authorized UCENA email.',
          entireAgreement: 'This Agreement constitutes the entire agreement between parties, superseding all prior agreements or understandings.'
        },
        acceptanceHeading: 'Acceptance and Signatures',
        acceptancePreface: 'The parties agree to the terms and conditions outlined herein:',
        signatoryName: 'Amoor Avakian',
        signatoryTitle: 'Managing Member',
        clientSignerTitle: 'Manager'
      }
    },
    // Additional configuration categories
    clientStatuses: [
      { id: 'active', name: 'Active', description: 'Currently engaged client', color: 'success' },
      { id: 'inactive', name: 'Inactive', description: 'Client not currently engaged', color: 'default' },
      { id: 'prospect', name: 'Prospect', description: 'Potential client', color: 'info' },
      { id: 'churned', name: 'Churned', description: 'Former client', color: 'error' },
      { id: 'onhold', name: 'On Hold', description: 'Temporarily paused', color: 'warning' }
    ],
    companySizes: [
      { id: 'small', name: 'Small', description: '1-50 employees', range: '1-50' },
      { id: 'medium', name: 'Medium', description: '51-200 employees', range: '51-200' },
      { id: 'large', name: 'Large', description: '201-1000 employees', range: '201-1000' },
      { id: 'enterprise', name: 'Enterprise', description: '1000+ employees', range: '1000+' }
    ],
    priorities: [
      { id: 'low', name: 'Low', description: 'Standard priority', color: 'default' },
      { id: 'medium', name: 'Medium', description: 'Moderate priority', color: 'info' },
      { id: 'high', name: 'High', description: 'High priority', color: 'warning' },
      { id: 'critical', name: 'Critical', description: 'Urgent attention required', color: 'error' }
    ],
    teamRoles: [
      { id: 'pm', name: 'Project Manager', description: 'Project oversight and coordination' },
      { id: 'dev', name: 'Developer', description: 'Software development' },
      { id: 'designer', name: 'Designer', description: 'UI/UX design' },
      { id: 'qa', name: 'QA Engineer', description: 'Quality assurance and testing' },
      { id: 'analyst', name: 'Data Analyst', description: 'Data analysis and reporting' },
      { id: 'ba', name: 'Business Analyst', description: 'Business requirements analysis' },
      { id: 'devops', name: 'DevOps Engineer', description: 'Infrastructure and deployment' }
    ],
    currencies: [
      { id: 'usd', code: 'USD', name: 'US Dollar', symbol: '$' },
      { id: 'eur', code: 'EUR', name: 'Euro', symbol: '€' },
      { id: 'gbp', code: 'GBP', name: 'British Pound', symbol: '£' },
      { id: 'jpy', code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
      { id: 'aud', code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
      { id: 'cad', code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' }
    ],
    entityTypes: [
      { id: 'team', name: 'Team', description: 'Internal team or department' },
      { id: 'supplier', name: 'Supplier Entity', description: 'External supplier or vendor' }
    ],
    paymentMethods: [
      { id: 'direct', name: 'Direct', description: 'Direct payment to individual' },
      { id: 'entity', name: 'Armenian Entity', description: 'Payment through Armenian entity' }
    ],
    // Cost categories for the new Costs-centric architecture
    costCategories: [
      { id: 'consult', name: 'CONSULT', description: 'Strategy sessions, marketing planning, brand audits, business consulting', icon: 'psychology' },
      { id: 'content', name: 'CONTENT', description: 'Blog writing, copywriting, scripts, newsletters', icon: 'article' },
      { id: 'design', name: 'DESIGN', description: 'Logos, brand kits, social media graphics, UI/UX assets', icon: 'palette' },
      { id: 'infra', name: 'INFRA', description: 'Server setup, DNS configuration, internal systems architecture', icon: 'dns' },
      { id: 'hosting', name: 'HOSTING', description: 'SiteGround, AWS, GoDaddy, domain management', icon: 'cloud' },
      { id: 'facebook', name: 'FACEBOOK', description: 'Facebook Ads, Page setup, pixel tracking', icon: 'facebook' },
      { id: 'google', name: 'GOOGLE', description: 'Google Ads, Google My Business, Analytics setup', icon: 'google' },
      { id: 'instagram', name: 'INSTAGRAM', description: 'Influencer coordination, content strategy, ad campaigns', icon: 'photo_camera' },
      { id: 'linkedin', name: 'LINKEDIN', description: 'B2B campaign setup, LinkedIn Ads, profile optimization', icon: 'business' },
      { id: 'meta', name: 'META', description: 'Cross-platform campaigns via Meta Business Suite', icon: 'campaign' },
      { id: 'email', name: 'EMAIL', description: 'Email marketing setup, Klaviyo, Mailchimp flows', icon: 'email' },
      { id: 'other', name: 'OTHER', description: 'Anything unique or ungrouped: sponsorships, app store optimization, etc.', icon: 'category' },
      { id: 'seo', name: 'SEO', description: 'On-page optimization, backlink building, keyword research', icon: 'search' },
      { id: 'web', name: 'WEB', description: 'Landing page creation, no-code tools like Webflow, Elementor builds', icon: 'web' },
      { id: 'video', name: 'VIDEO', description: 'Filming, editing, animation, post-production', icon: 'videocam' },
      { id: 'media', name: 'MEDIA', description: 'Paid placements, press, earned media, media buying', icon: 'newspaper' },
      { id: 'print', name: 'PRINT', description: 'Business cards, flyers, packaging, brochures', icon: 'print' },
      { id: 'development', name: 'DEVELOPMENT', description: 'Custom code, API integrations, backend builds, web apps', icon: 'code' }
    ],
    // Entity tags used in Entities create/edit
    entityTags: [
      { id: 'design', name: 'Design' },
      { id: 'operations', name: 'Operations' },
      { id: 'marketing', name: 'Marketing' },
      { id: 'development_tag', name: 'Development' },
      { id: 'finance', name: 'Finance' },
      { id: 'legal', name: 'Legal' },
      { id: 'consulting', name: 'Consulting' },
      { id: 'creative', name: 'Creative' },
      { id: 'technical', name: 'Technical' },
      { id: 'strategic', name: 'Strategic' }
    ],
    // Execution locations for cost delivery
    executionLocations: [
      { id: 'global', name: 'Global', description: 'Available worldwide' },
      { id: 'yerevan', name: 'Yerevan', description: 'Armenia' },
      { id: 'los_angeles', name: 'Los Angeles', description: 'California, USA' },
      { id: 'new_york', name: 'New York', description: 'New York, USA' },
      { id: 'london', name: 'London', description: 'United Kingdom' },
      { id: 'berlin', name: 'Berlin', description: 'Germany' },
      { id: 'dubai', name: 'Dubai', description: 'United Arab Emirates' }
    ],
    // Frequency options for costs, advertising budget, and external fees
    frequencyOptions: [
      { id: 'one_time', name: 'One-Time', description: 'Single occurrence' },
      { id: 'monthly', name: 'Monthly', description: 'Recurring monthly' },
      { id: 'yearly', name: 'Yearly', description: 'Recurring yearly' }
    ]
  };

  // Initialize configuration in Firestore if it doesn't exist
  const initializeConfig = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'appConfig', 'main'));
      if (!configDoc.exists()) {
        await setDoc(doc(db, 'appConfig', 'main'), {
          ...defaultConfig,
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        });
      } else {
        // Update existing document with any missing properties from defaultConfig
        const existingData = configDoc.data();
        const missingProperties = {};
        
        // Check for missing properties and add them
        Object.keys(defaultConfig).forEach(key => {
          if (!(key in existingData)) {
            missingProperties[key] = defaultConfig[key];
          }
        });
        
        // Update document if there are missing properties
        if (Object.keys(missingProperties).length > 0) {
          await updateDoc(doc(db, 'appConfig', 'main'), {
            ...missingProperties,
            lastUpdated: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error initializing config:', error);
    }
  };

  // Load configuration from Firestore
  useEffect(() => {
    const loadConfig = () => {
      const unsubscribe = onSnapshot(
        doc(db, 'appConfig', 'main'),
        (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            if (data.branding) writeCachedBranding(data.branding);
            // Merge Firebase data with default config to ensure new properties are available
            setConfig({
              ...defaultConfig,
              ...data,
              loading: false
            });
          } else {
            // Initialize with default config if document doesn't exist
            initializeConfig();
            setConfig({
              ...defaultConfig,
              loading: false
            });
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error loading config:', error);
          // Fallback to default config, keeping any cached branding
          setConfig({
            ...defaultConfig,
            branding: readCachedBranding() || defaultConfig.branding,
            loading: false
          });
          setLoading(false);
        }
      );

      return unsubscribe;
    };

    const unsubscribe = loadConfig();
    return () => unsubscribe();
  }, []);

  // Update configuration in Firestore
  const updateConfig = async (updates) => {
    try {
      const configRef = doc(db, 'appConfig', 'main');
      await updateDoc(configRef, {
        ...updates,
        lastUpdated: new Date().toISOString()
      });
      return true;
    } catch (error) {
      console.error('Error updating config:', error);
      return false;
    }
  };

  // Helper functions to get specific config data
  const getActiveCountries = () => {
    return config.countries?.filter(country => country.status === 'Active') || [];
  };

  const getActiveIndustries = () => {
    return config.industries?.filter(industry => industry.status === 'Active') || [];
  };

  const getCountryOptions = () => {
    return getActiveCountries();
  };

  const getIndustryOptions = () => {
    return getActiveIndustries().map(industry => industry.name);
  };

  const getClientStatusOptions = () => {
    return config.clientStatuses?.map(status => status.name) || [];
  };

  const getCompanySizeOptions = () => {
    return config.companySizes?.map(size => size.name) || [];
  };

  const getPriorityOptions = () => {
    return config.priorities?.map(priority => priority.name) || [];
  };

  const getTeamRoleOptions = () => {
    return config.teamRoles?.map(role => role.name) || [];
  };

  const getCurrencyOptions = () => {
    return config.currencies || [];
  };

  const getEntityTypeOptions = () => {
    return config.entityTypes?.map(type => type.name) || [];
  };

  const getPaymentMethodOptions = () => {
    return config.paymentMethods?.map(method => method.name) || [];
  };

  const getEntityTagOptions = () => {
    return config.entityTags?.map(tag => tag.name) || [];
  };

  const getBrandingConfig = () => {
    return config.branding || defaultConfig.branding;
  };

  const getPdfTexts = () => {
    return config.pdfTexts || defaultConfig.pdfTexts;
  };

  const getFinanceConfig = () => {
    return config.finance || defaultConfig.finance;
  };

  const getApiKeys = () => {
    return config.apiKeys || [];
  };

  const getLegalTexts = () => {
    return config.legal || [];
  };

  const getCostCategoryOptions = () => {
    return config.costCategories || [];
  };

  const getExecutionLocationOptions = () => {
    return config.executionLocations || [];
  };

  const getFrequencyOptions = () => {
    return config.frequencyOptions || [];
  };

  const value = {
    config,
    loading,
    // True once branding is usable: served from the local cache or loaded from Firestore
    brandingReady: !loading || hasCachedBranding,
    updateConfig,
    // Helper functions
    getActiveCountries,
    getActiveIndustries,
    getCountryOptions,
    getIndustryOptions,
    getClientStatusOptions,
    getCompanySizeOptions,
    getPriorityOptions,
    getTeamRoleOptions,
    getCurrencyOptions,
    getEntityTypeOptions,
    getPaymentMethodOptions,
    getEntityTagOptions,
    getBrandingConfig,
    getPdfTexts,
    getFinanceConfig,
    getApiKeys,
    getLegalTexts,
    getCostCategoryOptions,
    getExecutionLocationOptions,
    getFrequencyOptions
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export default ConfigContext; 